import { isNumber } from 'app/[lang]/[esite]/(core)/account/(with-nav)/saved-lists/utils';
import {
  TAX_EXCLUDED_PCC_TAX_DISCLAIMER,
  TAX_INCLUDED_PCC_TAX_DISCLAIMER
} from './constants';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { PRODUCT_TYPE } from 'constants/common';
import {
  PriceData,
  ProductsPriceData,
  SearchAddToCartItemData,
  SearchSelectItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { getSeoUrl, normalizeCommerceLink } from 'utils/links';
import {
  HEADLESS_PRODUCT_PAGE_PATH,
  HEADLESS_PRODUCT_TECHNOLOGY_PAGE_PATH
} from 'constants/links';
import { OrderItemPart } from 'services/cart/types';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { partTypeSoftware } from 'app/[lang]/[esite]/(core)/product/[product]/utils/commonUtils';
import { ProductPriceAvailabilityHeadless } from 'services/priceAvailability/types';

export const getEctMessageCode = ({
  isGstAvailable,
  isGstIncludesTax
}: {
  isGstAvailable: boolean;
  isGstIncludesTax: boolean;
}) => {
  const shouldShowGSTMessage = isGstAvailable === true;

  if (!shouldShowGSTMessage) {
    return;
  }

  if (shouldShowGSTMessage) {
    if (isGstIncludesTax === true) {
      return TAX_INCLUDED_PCC_TAX_DISCLAIMER;
    }

    if (isGstIncludesTax === false) {
      return TAX_EXCLUDED_PCC_TAX_DISCLAIMER;
    }
  }
};

export const shouldShowDiscountedPrice = (discountAmount, unitPrice) => {
  const discountAmountValue = discountAmount?.match(/\d+(\.\d+)?/)?.[0];
  const isValidDiscountAmount =
    isNumber(discountAmountValue) && parseFloat(discountAmountValue) > 0;

  return isValidDiscountAmount && !isEmpty(unitPrice);
};

export const productCategoryCommonLogic = (
  productPrice,
  isRegistrationPendingApproval,
  type
) => {
  const hasPrice =
    !!productPrice?.price.unitPrice && !isEmpty(productPrice?.price.unitPrice);
  const hasReplacement = productPrice?.replacementParts?.length > 0;
  const showAddCart =
    (hasPrice && !hasReplacement) || isRegistrationPendingApproval;
  const { availabilityMessage = '', showContactDealerPopUp = true } =
    productPrice?.availability?.[0] || {};
  const { availabilityBySource = [], storeAvailability = [] } =
    productPrice?.sourcingDetails || {};

  const isNonReturnable = productPrice?.partLevelFlags?.isNonReturnable;
  const isReplacedPart = productPrice?.replacementParts?.length > 0;
  const isRemanufacturedPart = productPrice?.partLevelFlags?.isRemanPart;
  const isIndirectReplacement =
    (isEmpty(productPrice?.price.unitPrice) && !hasReplacement) ||
    (productPrice?.partLevelFlags?.isIndirectReplacement ?? false);
  const isKit = type === PRODUCT_TYPE.PACKAGE_BEAN;
  const hasIcons =
    isNonReturnable || isReplacedPart || isRemanufacturedPart || isKit;
  return {
    showAddCart,
    hasReplacement,
    hasPrice,
    isIndirectReplacement,
    hasIcons,
    availabilityMessage,
    availabilityBySource,
    storeAvailability,
    showContactDealerPopUp,
    isNonReturnable,
    isReplacedPart,
    isKit,
    isRemanufacturedPart
  };
};

export const getPDPUrl = ({
  esiteParams,
  isHeadlessPDPEnabled,
  productNumber,
  productURL,
  partType,
  isProductTechnologyEnabled
}: {
  esiteParams: esiteParams | Record<string, string | string[]>;
  isHeadlessPDPEnabled: boolean;
  productNumber: string;
  productURL: string;
  partType?: string;
  isProductTechnologyEnabled?: boolean;
}) => {
  if (!isHeadlessPDPEnabled) {
    return normalizeCommerceLink(productURL);
  }

  const path =
    partTypeSoftware(partType) && isProductTechnologyEnabled
      ? `${HEADLESS_PRODUCT_PAGE_PATH}${HEADLESS_PRODUCT_TECHNOLOGY_PAGE_PATH}`
      : HEADLESS_PRODUCT_PAGE_PATH;

  return `${getSeoUrl(esiteParams)}${path}/${productNumber}`;
};

export const getFormattedSearchSelectItem = (
  orderItem:
    | OrderItemPart
    | { orderItemId?: string | number; orderId?: string | number },
  productPriceData: ProductsPriceData & ProductDealerPriceAvailability
): SearchSelectItemData => {
  const { orderItemId, orderId } = orderItem || {};
  const { partNumber, productPriceData: priceData } = productPriceData || {};

  const { quantity, weight, availability, unformattedTotalPrice } =
    priceData || {};

  return {
    orderItemId: orderItemId || orderId,
    partNumber: partNumber || productPriceData?.partNumber,
    quantity: quantity || productPriceData?.quantity,
    weight: weight?.toString() || productPriceData?.weight?.toString(),
    availabilityMessage:
      availability?.[0]?.availabilityMessage ||
      productPriceData?.availability?.[0]?.availabilityMessage,
    totalPrice:
      (unformattedTotalPrice && Number(unformattedTotalPrice)) ||
      (productPriceData?.unformattedUnitPrice &&
        Number(productPriceData?.unformattedUnitPrice))
  };
};

export const getFormattedSearchSelectItemHeadless = (
  orderItem:
    | OrderItemPart
    | { orderItemId?: string | number; orderId?: string | number },
  productPriceData: ProductsPriceData & ProductPriceAvailabilityHeadless
): SearchSelectItemData => {
  const { partNumber, productPriceData: priceData } = productPriceData || {};
  const { orderItemId, orderId } = orderItem || {};

  const { availability, quantity, unformattedTotalPrice, weight } =
    priceData || {};

  return {
    orderItemId: orderItemId || orderId,
    quantity: quantity || productPriceData?.quantity,
    weight: weight?.toString() || productPriceData?.weight?.toString(),
    partNumber: partNumber || productPriceData?.partNumber,
    availabilityMessage:
      availability?.[0]?.availabilityMessage ||
      productPriceData?.availability?.[0]?.availabilityMessage,
    totalPrice:
      (unformattedTotalPrice && Number(unformattedTotalPrice)) ||
      (productPriceData?.price?.unformatted?.unitPrice &&
        Number(productPriceData?.price?.unformatted?.unitPrice))
  };
};

export const getFormattedItemSummary = (
  productPriceData: ProductsPriceData
): Product => {
  const {
    productPriceData: priceData,
    serialNumber,
    fitmentCheck,
    listNameAndId,
    ...rest
  } = productPriceData;

  return { ...rest, categoryPath: priceData?.categoryPath };
};

export const getFormattedItemData = (
  cartId: string,
  productPriceData:
    | ProductsPriceData
    | PriceData[]
    | ProductDealerPriceAvailability[]
    | ProductPriceAvailabilityHeadless[],
  currency?: string
): SearchAddToCartItemData | SearchAddToCartItemData[] => {
  let priceData;
  if (productPriceData) {
    if (Array.isArray(productPriceData)) {
      priceData = productPriceData;
    } else {
      priceData = [productPriceData];
    }
  } else {
    priceData = [];
  }
  const itemPriceData = priceData?.map((item) => {
    return {
      orderId: cartId,
      orderItemPriceAvailabilityTotals: {
        currency: currency,
        orderItemPrices: [
          {
            priceWithoutGST: {
              unformatted: {
                unitPrice: item?.unformattedTotalPrice
                  ? Number(item?.unformattedTotalPrice)
                  : item?.price?.unformatted?.unitPrice
              }
            },
            availability: [
              {
                availabilityMessage:
                  item?.availability?.[0]?.availabilityMessage
              }
            ]
          }
        ]
      },
      orderItems: [
        {
          quantity: item?.quantity
        }
      ],
      partNumber: item?.partNumber
    };
  });

  return itemPriceData;
};

type GetSelectItem = {
  productPriceData: ProductsPriceData;
  locationId: string;
  cartId: string | number;
  serialNumber: string | undefined;
  fitmentCheck: string | undefined;
};
export const getSelectedItem = ({
  productPriceData,
  locationId,
  cartId,
  serialNumber,
  fitmentCheck
}: GetSelectItem) => ({
  item: getFormattedSearchSelectItem({ orderItemId: cartId }, productPriceData),
  partName: `${productPriceData.partNumber}: ${productPriceData.name}`,
  locationId,
  itemSummary: {
    categoryPath: productPriceData.categoryPath,
    manufacturer: productPriceData.manufacturer
  },
  isSearchPageEvent: true,
  itemListId: productPriceData.listNameAndId,
  itemListName: productPriceData.listNameAndId,
  index: productPriceData.index,
  serialNumber,
  fitment: fitmentCheck,
  ...(!!productPriceData.p_related_model && {
    relatedModel: productPriceData.p_related_model
  })
});
